const { libraryKey } = require('./libraryKey');

const metadataCache = new Map();
// Core exposes one shared MetaDetails state for this route, so metadata loads
// are intentionally serialized to avoid replacing an in-flight request.
const METADATA_CONCURRENCY = 1;
const SUPPORTED_LIBRARY_NAME_SORTS = Object.freeze(['name', 'namereverse']);

const toCarolineMetadata = (meta) => {
    if (meta === null || typeof meta !== 'object'
        || typeof meta.id !== 'string' || meta.id.length === 0
        || typeof meta.type !== 'string' || meta.type.length === 0) return null;

    return Object.freeze({
        id: meta.id,
        type: meta.type,
        name: typeof meta.name === 'string' ? meta.name : undefined,
        poster: typeof meta.poster === 'string' ? meta.poster : undefined,
        posterShape: ['poster', 'landscape', 'square'].includes(meta.posterShape) ? meta.posterShape : undefined,
        description: typeof meta.description === 'string' ? meta.description : undefined,
        deepLinks: meta.deepLinks && typeof meta.deepLinks === 'object' ? meta.deepLinks : undefined,
    });
};

const metadataCacheGet = (mediaType, metaId) => metadataCache.get(libraryKey(mediaType, metaId));
const metadataCacheSet = (mediaType, metaId, result) => {
    metadataCache.set(libraryKey(mediaType, metaId), Object.freeze(result));
};
const metadataCacheClear = () => metadataCache.clear();

const toCarolineLibraryViewModels = (references) => references.map((reference) => {
    const result = metadataCacheGet(reference.mediaType, reference.metaId);
    return Object.freeze({
        reference,
        metadata: result?.status === 'ready' ? result.metadata : null,
        metadataStatus: result?.status || 'loading',
    });
});

const createMetadataQueueState = (keys, limit = METADATA_CONCURRENCY, cachedKeys = []) => {
    const settled = new Set(cachedKeys);
    const active = new Set();
    for (const key of keys) {
        if (settled.has(key)) continue;
        active.add(key);
        if (active.size >= limit) break;
    }
    return { active, settled };
};

const settleMetadataQueueKey = (state, key, keys, limit = METADATA_CONCURRENCY) => {
    const settled = new Set(state.settled);
    const active = new Set(state.active);
    settled.add(key);
    active.delete(key);
    for (const nextKey of keys) {
        if (active.size >= limit) break;
        if (!settled.has(nextKey) && !active.has(nextKey)) active.add(nextKey);
    }
    return { active, settled };
};

const sortCarolineReferences = (references, metadataFor, sort) => {
    if (sort !== 'name' && sort !== 'namereverse') return references;
    const direction = sort === 'name' ? 1 : -1;
    return [...references].sort((left, right) => {
        const leftName = metadataFor(left)?.name || '';
        const rightName = metadataFor(right)?.name || '';
        const byName = leftName.localeCompare(rightName, undefined, { sensitivity: 'base' }) * direction;
        return byName || right.addedAt.localeCompare(left.addedAt);
    });
};

module.exports = {
    METADATA_CONCURRENCY,
    SUPPORTED_LIBRARY_NAME_SORTS,
    createMetadataQueueState,
    metadataCacheClear,
    metadataCacheGet,
    metadataCacheSet,
    settleMetadataQueueKey,
    sortCarolineReferences,
    toCarolineLibraryViewModels,
    toCarolineMetadata,
};
